import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Spacer,
  Text,
  Textarea,
  TextareaProps,
} from "@chakra-ui/react";
import { ChangeEvent, useCallback, useContext, useEffect } from "react";

import { RanaMarkdown } from "../../components/rana-markdown";
import { useFormFieldError } from "../hooks/use-rana-form-field-error";
import { RanaFormContext } from "../rana-form";

interface RanaFormTextareaProps extends TextareaProps {
  field: string;
  label?: string;
  explanation?: string;
  extra?: string;
  isPreviewingMarkdown?: boolean;
}

export function RanaFormTextarea({
  field,
  label,
  explanation,
  extra,
  isRequired,
  isPreviewingMarkdown,
  ...rest
}: RanaFormTextareaProps) {
  const { setPartialValues, values, addRequired } = useContext(RanaFormContext);

  const error = useFormFieldError(field);
  const value = values[field] ?? "";
  useEffect(() => {
    if (isRequired) addRequired(field);
  }, [addRequired, field, isRequired]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => setPartialValues({ [field]: e.target.value }),
    [field, setPartialValues]
  );

  return (
    <FormControl isRequired={isRequired} isInvalid={!!error}>
      <FormLabel>
        {label}
        <Spacer />
        <Text mt="auto" textStyle="sm">
          {extra}
        </Text>
      </FormLabel>
      <Box px={0}>
        {isPreviewingMarkdown ? (
          <Box mb="6" w="md" {...rest}>
            <RanaMarkdown markdown={value + ""} />
          </Box>
        ) : (
          <Textarea
            rounded="base"
            w="md"
            mb="6"
            bg="brand.grey.100"
            isInvalid={!!error}
            placeholder={explanation}
            {...rest}
            value={value + ""}
            onChange={onChange}
            zIndex="base"
          />
        )}
      </Box>
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  );
}
